/**
 * Team chat model: pure helpers shared by the sidebar, the channel view and the
 * thread panel. Conversations come from GET /api/groups (`kind` "channel" or
 * "dm"); messages from the group messages and thread endpoints.
 */

/** Mention entries for "@canal" and "@aqui" in the editor's suggestion list. */
export const BROADCAST_MENTIONS = [
  { id: '__canal__', broadcast: 'channel', fullName: 'canal', label: 'canal', hint: 'Avisa todos os membros do canal' },
  { id: '__aqui__', broadcast: 'here', fullName: 'aqui', label: 'aqui', hint: 'Avisa só quem está online agora' },
];

const BROADCAST_BY_ID = new Map(BROADCAST_MENTIONS.map((m) => [m.id, m.broadcast]));

/** @param {{ kind?: string } | null | undefined} group */
export function isDirectMessage(group) {
  return group?.kind === 'dm';
}

/**
 * A message shows in the channel when it is not a thread reply, or when it is a
 * reply that was also sent to the channel. Mirrors the API's rule.
 */
export function isChannelVisible(message) {
  return !message?.threadRootId || message.alsoSentToChannel === true;
}

function firstName(user) {
  return (user?.fullName || user?.name || 'Usuário').split(' ')[0];
}

/**
 * A DM has no name of its own: one other person shows their full name, a group
 * DM their first names.
 */
export function dmDisplayName(group) {
  const others = group?.dmMembers || [];
  if (others.length === 0) return 'Mensagem direta';
  if (others.length === 1) return others[0].fullName || 'Usuário';
  return others.map(firstName).join(', ');
}

/** Longest name of a channel born from a group DM (the API refuses more). */
export const CHANNEL_NAME_MAX_LENGTH = 80;

/**
 * Only a group DM (3 people or more, you included) can become a channel: a
 * conversation between two people cannot.
 * @param {{ kind?: string, dmMembers?: unknown[] } | null | undefined} group
 */
export function canConvertToChannel(group) {
  return isDirectMessage(group) && (group.dmMembers?.length || 0) >= 2;
}

/** The name the conversion dialog starts from: the people in it. */
export function suggestChannelName(group) {
  return dmDisplayName(group).slice(0, CHANNEL_NAME_MAX_LENGTH).trim();
}

/**
 * What the person reads when the conversion fails. The API's reason is in English
 * and meant for logs: a missing permission is the one failure they can act on.
 * @param {Error | null | undefined} error
 */
export function convertErrorMessage(error) {
  if (/FORBIDDEN|Insufficient permissions/.test(error?.message || '')) {
    return 'Você não tem permissão para criar canais. Peça a um administrador.';
  }
  return 'Não foi possível converter a conversa. Confira o nome e tente de novo.';
}

/** The title of any conversation: "#name" is up to the caller. */
export function conversationTitle(group) {
  if (!group) return '';
  return isDirectMessage(group) ? dmDisplayName(group) : group.name;
}

/** "Online", "Ausente" or "Offline", as the people picker and DM header say it. */
export function presenceLabel(user) {
  if (!user?.isOnline) return 'Offline';
  return user.presenceStatus === 'away' ? 'Ausente' : 'Online';
}

const byName = (a, b) => (a.name || '').localeCompare(b.name || '', 'pt-BR');

/**
 * A channel that sits in the person's own list: one they joined, or, for platform staff
 * supporting the account, any channel (the API marks those `supportAccess`: they read and
 * write everywhere without being members).
 */
const inTeamList = (g) => !!g.isMember || !!g.supportAccess;

/**
 * Splits the conversation list for the sidebar:
 * - channels: joined channels (every channel for staff supporting the account), by name;
 * - dms: most recent activity first;
 * - browsable: public channels the user has not joined ("Navegar pelos canais").
 */
export function splitConversations(groups = []) {
  const channels = [];
  const dms = [];
  const browsable = [];
  for (const g of groups) {
    if (isDirectMessage(g)) dms.push(g);
    else if (inTeamList(g)) channels.push(g);
    else if (g.visibility === 'public') browsable.push(g);
  }
  channels.sort(byName);
  dms.sort((a, b) => {
    const at = a.lastMessageAt || a.createdAt || 0;
    const bt = b.lastMessageAt || b.createdAt || 0;
    return new Date(bt) - new Date(at) || dmDisplayName(a).localeCompare(dmDisplayName(b), 'pt-BR');
  });
  browsable.sort(byName);
  return { channels, dms, browsable };
}

/**
 * How a sidebar item shows its unread state, as in a messaging app: every unread message
 * counts, in a channel and in a DM (a channel also notifies for every message, unless muted):
 * - the open conversation shows nothing (it is being read);
 * - a muted channel is dimmed and never bold nor badged;
 * - anything else is bold when unread and badged with its unread messages.
 *
 * @returns {{ bold: boolean, dimmed: boolean, badge: number }}
 */
export function sidebarUnreadState(group, { active = false } = {}) {
  if (active) return { bold: false, dimmed: false, badge: 0 };
  const unread = group?.unreadCount || 0;
  if (isDirectMessage(group)) return { bold: unread > 0, dimmed: false, badge: unread };
  if (group?.isMuted) return { bold: false, dimmed: true, badge: 0 };
  return { bold: unread > 0, dimmed: false, badge: unread };
}

/**
 * Splits the editor's mention ids into people and the broadcast ("@canal" wins
 * over "@aqui" when both are typed).
 *
 * @param {string[]} ids
 * @returns {{ mentionedUserIds: string[], broadcast: 'channel' | 'here' | null }}
 */
export function splitMentions(ids = []) {
  const people = [];
  let broadcast = null;
  for (const id of ids) {
    const kind = BROADCAST_BY_ID.get(id);
    if (kind) broadcast = broadcast === 'channel' ? 'channel' : kind;
    else if (!people.includes(id)) people.push(id);
  }
  return { mentionedUserIds: people, broadcast };
}

/** "1 resposta", "3 respostas". */
export function replyCountLabel(count) {
  return count === 1 ? '1 resposta' : `${count} respostas`;
}

/**
 * Index (in `messages`) of the first unread message, given how many were unread
 * when the conversation was opened. Counts only others' channel messages, from
 * the end, as the API counts them.
 *
 * @param {object[]} messages - chronological
 * @param {number} unreadCount
 * @param {string} selfId
 * @returns {number} -1 when there is nothing to mark
 */
export function firstUnreadIndex(messages, unreadCount, selfId) {
  if (!unreadCount || unreadCount <= 0) return -1;
  let remaining = unreadCount;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m.deleted || m.senderId === selfId || !isChannelVisible(m)) continue;
    remaining -= 1;
    if (remaining === 0) return i;
  }
  return -1;
}

/**
 * The text a conversation row previews, by the same rule as the API's
 * `lastMessage.preview`: the editor's tags stripped, 100 characters, a file with
 * no text named.
 *
 * @param {string | null | undefined} content
 * @param {string} [type]
 * @returns {string}
 */
export function messagePreviewText(content, type) {
  const text = (content || '').replace(/<[^>]*>/g, '').trim().slice(0, 100);
  if (text) return text;
  return type && type !== 'text' ? 'enviou um arquivo' : '';
}

/** When a conversation last had news: its newest message, or when it was created (ms). */
export function conversationActivityAt(group) {
  return new Date(group?.lastMessage?.createdAt || group?.lastMessageAt || group?.createdAt || 0).getTime();
}

/**
 * The conversations of the team chat a person is in (joined channels and every DM),
 * newest activity first, ready to sit among the attendances. The channels platform staff
 * reach as support are not here (they are not theirs): the "+" finds them.
 *
 * @param {object[]} [groups] - GET /api/groups
 * @returns {object[]}
 */
export function teamConversations(groups = []) {
  return groups
    .filter((g) => isDirectMessage(g) || g.isMember)
    .sort((a, b) => conversationActivityAt(b) - conversationActivityAt(a) || byName(a, b));
}

/**
 * Applied filters that keep the team conversations among the attendances: none, or only
 * "open, mine or anyone's". "Responsável: Todos" is what the old "Todas" tab became, a
 * wider "Minhas", and the person's own conversations belong there too. Any other cut
 * (tag, channel, priority, another person, the queue, dates...) is about attendances,
 * and the team conversations leave the list.
 *
 * @param {object} [filters] - useInboxFilters().filters (a view's filters when one is applied)
 * @returns {boolean}
 */
export function filtersKeepTeamConversations(filters = {}) {
  return Object.entries(filters || {}).every(([key, value]) => {
    if (value === undefined || value === null || value === '') return true;
    if (Array.isArray(value) && value.length === 0) return true;
    if (key === 'status') return value === 'open';
    if (key === 'assignedTo') return value === 'me' || value === 'any';
    return false;
  });
}

/**
 * Puts the team conversations among the attendances by last activity, both newest
 * first. Every team conversation is always in the list: on a phone this list is the
 * only way to reach them, so one older than the last loaded attendance cannot wait
 * for the remaining pages. It sits after the last loaded attendance, and moves down
 * as more attendances load above it.
 *
 * @param {object[]} tickets - in the order the list shows them
 * @param {object[]} teams - from `teamConversations`
 * @param {{ ticketTime: (ticket: object) => number }} options
 * @returns {Array<{ kind: 'ticket', ticket: object } | { kind: 'team', group: object }>}
 */
export function mergeConversationRows(tickets, teams, { ticketTime }) {
  const rows = [];
  let next = 0;
  for (const ticket of tickets) {
    const at = ticketTime(ticket);
    while (next < teams.length && conversationActivityAt(teams[next]) >= at) {
      rows.push({ kind: 'team', group: teams[next] });
      next += 1;
    }
    rows.push({ kind: 'ticket', ticket });
  }
  for (; next < teams.length; next += 1) rows.push({ kind: 'team', group: teams[next] });
  return rows;
}

/**
 * What the row of a team conversation says under its name: the last message, with who
 * wrote it ("Você", or a first name in a channel or a group DM; a conversation of two
 * needs no name). Without messages, the channel's description or a plain note.
 *
 * @param {object} group
 * @param {string | null} selfId
 * @returns {{ sender: string | null, text: string }}
 */
export function teamPreview(group, selfId) {
  const last = group?.lastMessage;
  if (!last) return { sender: null, text: group?.description || 'Nenhuma mensagem ainda' };
  const mine = !!selfId && last.senderId === selfId;
  const oneToOne = isDirectMessage(group) && (group.dmMembers?.length || 0) === 1;
  let sender = null;
  if (mine) sender = 'Você';
  else if (!oneToOne && last.senderName) sender = firstName({ fullName: last.senderName });
  return { sender, text: last.preview || '' };
}

/** The signed-in user's database id, as `/auth/me` returns it. */
export function authUserId(user) {
  return user?.userId || user?.localUserId || user?.id || null;
}

/**
 * The team chat conversation that is actually on screen, or null. Only the 'group'
 * view shows one, and on the phone only while the chat screen (not the list) is up:
 * going back to the list leaves the view on 'group' but shows nothing of it, and a
 * message that arrives then is news, not something already read.
 *
 * @param {object} params
 * @param {string} params.currentView - the Inbox view ('attendance', 'group', ...)
 * @param {string | null} params.selectedGroupId
 * @param {boolean} params.isMobile
 * @param {'list' | 'chat'} params.mobileView
 * @returns {string | null}
 */
export function onScreenConversationId({ currentView, selectedGroupId, isMobile, mobileView }) {
  if (currentView !== 'group' || !selectedGroupId) return null;
  return isMobile && mobileView !== 'chat' ? null : selectedGroupId;
}

/**
 * Is the person already looking at what a notification is about? Then a toast
 * (and its sound) would only repeat what is on screen. A message in the open
 * conversation qualifies; a reply in a thread qualifies only when that thread is
 * the one open, because a reply in a thread you are not looking at is news even
 * inside the open channel.
 *
 * @param {{ groupId?: string, threadRootId?: string } | null | undefined} metadata - the notification's
 * @param {{ groupId: string | null, threadRootId: string | null }} open - what is on screen
 */
export function isViewingConversation(metadata, open) {
  if (!metadata?.groupId || metadata.groupId !== open?.groupId) return false;
  if (!metadata.threadRootId) return true;
  return metadata.threadRootId === open.threadRootId;
}

/**
 * How long a conversation can be muted for, in the order the menu offers them. `phrase` is what
 * the confirmation says ("Canal silenciado por 8 horas"). The API takes `durationMinutes` or
 * `forever`.
 */
export const MUTE_OPTIONS = [
    { key: '15m', label: '15 minutos', phrase: 'por 15 minutos', durationMinutes: 15 },
    { key: '1h', label: '1 hora', phrase: 'por 1 hora', durationMinutes: 60 },
    { key: '8h', label: '8 horas', phrase: 'por 8 horas', durationMinutes: 480 },
    { key: '1d', label: '1 dia', phrase: 'por 1 dia', durationMinutes: 1440 },
    { key: '1w', label: '1 semana', phrase: 'por 1 semana', durationMinutes: 10080 },
    { key: 'forever', label: 'Sempre', phrase: 'para sempre', forever: true },
];

/** The API stores "forever" as a date far past any real one. */
const FOREVER_FROM_YEAR = 2090;

/**
 * Until when a muted conversation is silent, for the menu: "para sempre", "até 18:30" (today)
 * or "até 27/10, 18:30".
 *
 * @param {string | Date | null | undefined} mutedUntil
 * @param {Date} [now]
 * @returns {string | null} null when it is not muted
 */
export function muteUntilLabel(mutedUntil, now = new Date()) {
    if (!mutedUntil) return null;
    const end = new Date(mutedUntil);
    if (Number.isNaN(end.getTime()) || end <= now) return null;
    if (end.getFullYear() >= FOREVER_FROM_YEAR) return 'para sempre';
    const time = end.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    if (end.toDateString() === now.toDateString()) return `até ${time}`;
    const day = end.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    return `até ${day}, ${time}`;
}
